import React, { useEffect, useMemo, useRef, useState } from 'react';
import ActivitySocialBar from './ActivitySocialBar';
import ActivityPositionPin, { validPosition } from './ActivityPositionPin';
import { ActivityHeaderSummary, ActivityStatusBullet, isDefaultExpanded } from './ActivityHeaderSummary';
import { getDojoActivityStatus } from './dojoActivityStatus';

const memberId = user => String(
  user && typeof user === 'object' ? (user._id || user.user_id || user.id || '') : (user || '')
);
const activityOwnerId = activity => memberId(activity?.user_id);

export const peerActivityDisclosureTargetId = activity => String(
  getDojoActivityStatus(activity).clickedActivity?._id || activity?._id || ''
);

export default function PeerActivityDisclosure(props) {
  const identity = `${props.activity?._id}:${peerActivityDisclosureTargetId(props.activity)}:${memberId(props.owner)}:${props.activity?.type}`;
  return <PeerActivityDisclosureRow key={identity} {...props} />;
}

function PeerActivityDisclosureRow({
  activity,
  owner,
  backendService,
  renderActivityDetail,
  renderActivityName,
  selectedMemberContext = false,
}) {
  const presentation = useMemo(() => getDojoActivityStatus(activity), [activity]);
  const targetActivity = presentation.clickedActivity || activity;
  const activityId = String(activity?._id || '');
  const targetId = String(targetActivity?._id || '');
  const ownerId = memberId(owner);
  const expectedType = targetActivity?.type || activity?.type;
  const disclosureIdentity = `${targetId}:${ownerId}:${expectedType || ''}`;
  const name = activity?.name || 'Activity';
  const ownerName = owner?.display_name || 'Dojo member';
  const contentId = `peer-activity-detail-${activityId}`;
  const [expandOverride, setExpanded] = useState(null);
  const expanded = expandOverride ?? (selectedMemberContext && isDefaultExpanded(targetActivity));
  const [detailRecord, setDetailRecord] = useState(null);
  const [state, setState] = useState('idle');
  const requestRef = useRef(0);
  const detail = detailRecord?.identity === disclosureIdentity ? detailRecord.activity : null;
  const headerActivity = detail
    ? {
        ...targetActivity,
        ...detail,
        // A newer feed revocation must not be overridden by cached detail;
        // a newer detail revocation must not inherit stale feed coordinates.
        position: validPosition(targetActivity.position) && validPosition(detail.position) ? detail.position : undefined,
        ...(detail.nutrition_summary_unavailable
          && targetActivity.nutrition_summary
          && !targetActivity.nutrition_summary_unavailable
          ? { nutrition_summary: targetActivity.nutrition_summary, nutrition_summary_unavailable: false }
          : {}),
      }
    : targetActivity;

  const toggle = () => setExpanded(!expanded);

  useEffect(() => {
    if (!expanded) return undefined;
    if (detail) {
      setState('ready');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const request = ++requestRef.current;
      setState('loading');
      try {
        const response = await backendService?.getSocialActivity?.(targetId);
        if (cancelled || request !== requestRef.current) return;
        const responseActivity = response?.activity;
        const responseOwnerId = memberId(response?.owner);
        if (
          !targetId || !ownerId
          || String(responseActivity?._id || '') !== targetId
          || activityOwnerId(responseActivity) !== ownerId
          || responseOwnerId !== ownerId
          || responseActivity?.type !== expectedType
        ) {
          throw new Error('Peer activity identity mismatch');
        }
        setDetailRecord({ identity: disclosureIdentity, activity: responseActivity });
        setState('ready');
      } catch (error) {
        if (cancelled || request !== requestRef.current) return;
        setState('error');
      }
    };
    load();
    return () => { cancelled = true; requestRef.current += 1; };
  }, [expanded, detail, targetId, ownerId, expectedType, disclosureIdentity, backendService]);

  return (
    <div
      id={`activity-${activityId}`}
      className={`todo-activity todo-peer-activity ${presentation.status === 'done' ? 'done' : ''} ${presentation.status === 'rest' ? 'rest' : ''} ${presentation.status === 'missed' ? 'failed' : ''} ${presentation.status === 'live' ? 'ongoing-workout' : ''} ${expanded ? 'expanded' : ''}`}
    >
      <div className={`todo-peer-activity-header ${validPosition(headerActivity.position) ? 'has-position' : ''}`}>
        <button
          type="button"
          className="todo-activity-row"
          aria-expanded={expanded}
          aria-controls={contentId}
          aria-label={`${name}. ${presentation.label}${selectedMemberContext ? '' : `. Shared by ${ownerName}`}`}
          onClick={toggle}
        >
          <ActivityStatusBullet activity={activity} />
          <span className="todo-name">
            {renderActivityName ? renderActivityName(name) : <span className="todo-name-text">{name}</span>}
            {!selectedMemberContext && <span className="todo-media-owner">{ownerName} · Shared activity</span>}
          </span>
          <ActivityHeaderSummary activity={headerActivity} peer />
          <span className={`todo-chevron ${expanded ? 'open' : ''}`} aria-hidden="true">›</span>
        </button>
        <ActivityPositionPin activity={headerActivity} />
      </div>
      {expanded && (
        <div id={contentId} className="todo-content peer-activity-content">
          {state === 'loading' && <p className="nutrition-unknown" role="status">{selectedMemberContext ? 'Loading activity…' : 'Loading shared activity…'}</p>}
          {state === 'error' && <p className="nutrition-unknown" role="alert">{selectedMemberContext ? 'Activity details are unavailable.' : 'Shared activity details are unavailable.'}</p>}
          {state === 'ready' && detail && (
            <div data-peer-detail={targetId}>
              {renderActivityDetail?.(detail)}
              <ActivitySocialBar
                activityId={detail._id}
                social={detail.social}
                backendService={backendService}
                activityStatus={['complete', 'completed'].includes(detail.status) ? 'complete' : detail.status}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
